import { getMathFingerprint } from './math.js';

const VOICES_CACHE_PREFIX = 'voices';
const VOICES_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const VOICES_COLLECT_WAIT_MS = 1000;
const VOICES_BACKGROUND_READ_DELAYS_MS = [350, 1500];

const speechVoicesState = {
  started: false,
  bestVoices: [],
  cacheVoices: null,
  liveReadyPromise: null,
  resolveLiveReady: null,
};

/** Returns voices, performance, math, applePay only. indexedDB, fileSystem, and features.storage come from phase-1 in persistent-storage (see rs-sdk). */
export default async function getFeaturesFP(opts) {
  const o = opts || {};
  const want = (key) => o[key] !== false;
  const [voices, performance, math, applePay] = await Promise.all([
    want('voices') ? getSpeechVoices() : undefined,
    want('performance') ? measurePerformance() : undefined,
    want('math') ? mathFunctions() : undefined,
    location.protocol === 'https:' ? getApplePay() : {},
  ]);
  const out = {};
  if (voices !== undefined) out.voices = voices;
  if (performance !== undefined) out.performance = performance;
  if (math !== undefined) out.math = math;
  out.applePay = applePay;
  return out;
}

export function primeSpeechVoices() {
  const synth = getSpeechSynthesis();
  if (!synth) {
    return null;
  }

  if (speechVoicesState.started) {
    return speechVoicesState;
  }

  speechVoicesState.started = true;
  speechVoicesState.cacheVoices = loadCachedVoiceUris();
  speechVoicesState.liveReadyPromise = new Promise((resolve) => {
    speechVoicesState.resolveLiveReady = resolve;
  });

  if ('function' == typeof synth.addEventListener) {
    synth.addEventListener('voiceschanged', handleVoicesChanged);
  }

  scheduleBackgroundVoiceReads();
  return speechVoicesState;
}

async function getSpeechVoices() {
  try {
    const synth = getSpeechSynthesis();
    if (!synth) return null;

    const state = primeSpeechVoices();
    const immediate = updateVoicesFromLive();
    if (immediate.length > 0) {
      return immediate;
    }

    if (state?.cacheVoices?.length > 0) {
      return state.cacheVoices.slice();
    }

    if ('function' != typeof synth.addEventListener) {
      return immediate;
    }

    const liveVoices = await waitForLiveVoices(VOICES_COLLECT_WAIT_MS);
    if (liveVoices.length > 0) {
      return liveVoices;
    }

    const finalRead = updateVoicesFromLive();
    if (finalRead.length > 0) {
      return finalRead;
    }

    return [];
  } catch (error) {
    return [];
  }
}

function getSpeechSynthesis() {
  if (typeof window === 'undefined') return null;
  if (!window.speechSynthesis) return null;
  if ('function' != typeof window.speechSynthesis.getVoices) return null;

  return window.speechSynthesis;
}

function handleVoicesChanged() {
  updateVoicesFromLive();
}

function scheduleBackgroundVoiceReads() {
  for (let i = 0; i < VOICES_BACKGROUND_READ_DELAYS_MS.length; i++) {
    const delay = VOICES_BACKGROUND_READ_DELAYS_MS[i];
    setTimeout(function () {
      updateVoicesFromLive();
    }, delay);
  }
}

async function waitForLiveVoices(timeoutMs) {
  if (speechVoicesState.bestVoices.length > 0) {
    return speechVoicesState.bestVoices.slice();
  }

  if (!speechVoicesState.liveReadyPromise) {
    return [];
  }

  return Promise.race([
    speechVoicesState.liveReadyPromise,
    new Promise((resolve) => setTimeout(function () {
      resolve([]);
    }, timeoutMs)),
  ]);
}

function updateVoicesFromLive() {
  const voiceUris = toVoiceUris(safeGetVoices());
  if (voiceUris.length === 0) {
    return [];
  }

  if (!areVoiceUriArraysEqual(speechVoicesState.bestVoices, voiceUris)) {
    speechVoicesState.bestVoices = voiceUris.slice();
    persistVoiceUris(voiceUris);
  }

  if (speechVoicesState.resolveLiveReady) {
    speechVoicesState.resolveLiveReady(voiceUris.slice());
    speechVoicesState.resolveLiveReady = null;
  }

  return voiceUris.slice();
}

function safeGetVoices() {
  try {
    const list = window.speechSynthesis.getVoices();
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
}

function toVoiceUris(list) {
  const voiceUris = [];

  for (let i = 0; i < list.length; i++) {
    let voice;
    try {
      voice = list[i];
    } catch (error) {
      continue;
    }

    if (!voice || 'object' != typeof voice) {
      continue;
    }

    let voiceURI;
    try {
      voiceURI = voice.voiceURI;
    } catch (error) {
      continue;
    }

    if ('string' == typeof voiceURI) {
      voiceUris.push(voiceURI);
    }
  }

  return voiceUris;
}

function areVoiceUriArraysEqual(left, right) {
  if (left.length !== right.length) {
    return false;
  }

  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }

  return true;
}

function persistVoiceUris(voiceUris) {
  const cacheKey = getVoicesCacheKey();
  if (!cacheKey) {
    return;
  }

  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        savedAt: Date.now(),
        voiceUris: voiceUris.slice(),
      })
    );
  } catch (error) {}
}

function loadCachedVoiceUris() {
  const cacheKey = getVoicesCacheKey();
  if (!cacheKey) {
    return null;
  }

  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || 'object' != typeof parsed) {
      return null;
    }
    if ('number' != typeof parsed.savedAt) {
      return null;
    }
    if (Date.now() - parsed.savedAt > VOICES_CACHE_MAX_AGE_MS) {
      return null;
    }
    if (!Array.isArray(parsed.voiceUris)) {
      return null;
    }

    const sanitized = parsed.voiceUris.filter(function (voiceUri) {
      return 'string' == typeof voiceUri && voiceUri.length > 0;
    });

    return sanitized.length > 0 ? sanitized : null;
  } catch (error) {
    return null;
  }
}

function getVoicesCacheKey() {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return null;
    }

    const storage = window.localStorage;
    if (!storage) {
      return null;
    }

    const platform = navigator.platform || '';
    const language = navigator.language || '';
    const languages = navigator.languages ? navigator.languages.join(',') : '';
    const userAgent = navigator.userAgent || '';

    return [VOICES_CACHE_PREFIX, platform, language, languages, userAgent].join('|');
  } catch (error) {
    return null;
  }
}

async function measurePerformance() {
  var e = window.performance;

  for (var n = 1, t = 1, start = e.now(), end = start, i = 0; i < 5e4; i++)
    if ((start = end) < (end = e.now())) {
      var a = end - start;
      if (a > n) {
        if (a < t) t = a;
      } else if (a < n) {
        t = n;
        n = a;
      }
    }

  return [n, t];
}

async function mathFunctions() {
  return getMathFingerprint();
}

async function getApplePay() {
  let res = {};

  if (typeof window.ApplePaySession !== 'undefined' && window.ApplePaySession) {
    try {
      if (typeof window.ApplePaySession.canMakePayments !== 'undefined') {
        res.canMP = window.ApplePaySession.canMakePayments();
      }
    } catch (e) {
      // e.g. InvalidAccessError: Apple Pay from an insecure document — record nothing, don't throw.
    }

    try {
      if (typeof window.ApplePaySession.canMakePaymentsWithActiveCard !== 'undefined') {
        res.canMPWAC = await window.ApplePaySession.canMakePaymentsWithActiveCard('');
      }
    } catch (e) {
      /* ignore */
    }
  }

  return res;
}
